import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import { Workflow, Plus, Trash2, Loader2, Activity, LogIn, UserPlus } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { TopNav } from '../ui/top-nav';
import { ThemeToggle } from '../theme/theme-toggle';
import { APP_VERSION } from '../../version';
import { flowTemplates } from '../../config/flowTemplates';
import { FlowCard } from './FlowCard';
import { FlowCategory, SavedFlowSummary } from '../../types/flows';
import { flowService } from '../../services/flowService';

const categories: { label: string; value: FlowCategory | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Audio', value: 'audio' },
  { label: 'Document', value: 'document' },
  { label: 'Data', value: 'data' },
  { label: 'Video', value: 'video' },
  { label: 'Research', value: 'research' },
  { label: 'API', value: 'api' },
];

const relativeTime = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

export const FlowsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<FlowCategory | 'all'>('all');
  const [savedFlows, setSavedFlows] = useState<SavedFlowSummary[]>([]);
  const [loadingFlows, setLoadingFlows] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    getCurrentUser()
      .then(() => setIsAuthenticated(true))
      .catch(() => setIsAuthenticated(false));
  }, []);

  const loadFlows = useCallback(async () => {
    try {
      const flows = await flowService.listFlows();
      setSavedFlows(flows);
    } catch {
      // ignore
    } finally {
      setLoadingFlows(false);
    }
  }, []);

  // Only load saved flows if authenticated
  useEffect(() => {
    if (isAuthenticated) loadFlows();
    else setLoadingFlows(false);
  }, [isAuthenticated, loadFlows]);

  const handleNewFlow = async () => {
    const name = window.prompt('Flow name:');
    if (!name?.trim()) return;
    setCreating(true);
    try {
      const flow = await flowService.createFlow({
        name: name.trim(),
        nodes: [],
        edges: [],
        color: 'blue',
        color_hex: '#3b82f6',
      });
      navigate(`/flows/${flow.flow_id}`);
    } catch {
      // ignore
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, flowId: string) => {
    e.stopPropagation();
    if (!window.confirm('Delete this flow?')) return;
    setDeletingId(flowId);
    try {
      await flowService.deleteFlow(flowId);
      setSavedFlows((prev) => prev.filter((f) => f.flow_id !== flowId));
    } catch {
      // ignore
    } finally {
      setDeletingId(null);
    }
  };

  const filtered =
    activeCategory === 'all'
      ? flowTemplates
      : flowTemplates.filter((t) => t.category === activeCategory);

  return (
    <div className="min-h-screen bg-background">
      {isAuthenticated ? (
        <TopNav />
      ) : (
        <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-14 items-center justify-between px-4">
            <Link to="/flows" className="flex items-center gap-2">
              <Activity className="h-6 w-6 text-primary" />
              <span className="font-bold text-lg">BigSplice</span>
            </Link>
            <div className="flex items-center gap-1.5 sm:gap-3">
              <span className="text-xs text-muted-foreground hidden sm:inline">v{APP_VERSION}</span>
              <ThemeToggle />
              <Button variant="ghost" size="sm" className="text-sm" onClick={() => navigate('/login')}>
                <LogIn className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Sign In</span>
              </Button>
              <Button size="sm" className="text-sm" onClick={() => navigate('/register')}>
                <UserPlus className="h-4 w-4 mr-1.5" />
                <span className="hidden sm:inline">Sign Up Free</span>
                <span className="sm:hidden">Sign Up</span>
              </Button>
            </div>
          </div>
        </header>
      )}

      <main className="overflow-auto p-6" style={{ height: 'calc(100vh - 4.75rem)' }}>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Workflow className="h-5 w-5 text-primary" />
            <div className="flex-1">
              <h1 className="text-base font-semibold">Flows</h1>
              <p className="text-sm text-muted-foreground">
                Build data pipelines from raw data to insights
              </p>
            </div>
          </div>
        </div>

        {/* My Flows section — only shown when authenticated */}
        {isAuthenticated && (
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">My Flows</h2>
              <Button
                size="sm"
                className="text-xs h-8"
                onClick={handleNewFlow}
                disabled={creating}
              >
                {creating ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                )}
                New Flow
              </Button>
            </div>

            {loadingFlows ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : savedFlows.length === 0 ? (
              <div className="border border-dashed rounded-xl py-8 text-center">
                <p className="text-sm text-muted-foreground">
                  No saved flows yet. Create one or start from a template below.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {savedFlows.map((flow) => (
                  <button
                    key={flow.flow_id}
                    onClick={() => navigate(`/flows/${flow.flow_id}`)}
                    className="group text-left w-full rounded-xl border-2 border-border/50 p-4 transition-all hover:shadow-lg hover:scale-[1.02] hover:border-primary/30"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: flow.color_hex }}
                      >
                        <span className="text-white text-xs font-bold">
                          {flow.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate">{flow.name}</h3>
                        <p className="text-[11px] text-muted-foreground">
                          {relativeTime(flow.updated_at)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => handleDelete(e, flow.flow_id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-destructive/10"
                        disabled={deletingId === flow.flow_id}
                      >
                        {deletingId === flow.flow_id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {flow.node_count} nodes
                      </Badge>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {flow.edge_count} edges
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Templates section */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Templates</h2>

          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((cat) => (
              <Badge
                key={cat.value}
                variant={activeCategory === cat.value ? 'default' : 'outline'}
                className="cursor-pointer text-xs px-3 py-2 sm:py-1 transition-colors"
                onClick={() => setActiveCategory(cat.value)}
              >
                {cat.label}
              </Badge>
            ))}
          </div>

          {/* Template grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((t) => (
              <FlowCard key={t.id} template={t} />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};
